import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UwIcon } from '../icon/uw-icon';

export type UwDateMode = 'single' | 'range';

export type UwDateSingleValue = string | null;

export type UwDateRangeValue = {
  start: string | null;
  end: string | null;
};

export type UwDateValue = UwDateSingleValue | UwDateRangeValue;

type CalendarDay = {
  iso: string;
  day: number;
  inMonth: boolean;
  disabled: boolean;
  isToday: boolean;
  isSelected: boolean;
  isRangeStart: boolean;
  isRangeEnd: boolean;
  isInRange: boolean;
};

type RangeField = 'start' | 'end';

@Component({
  selector: 'uw-date',
  standalone: true,
  imports: [CommonModule, UwIcon],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UwDate),
      multi: true,
    },
  ],
  template: `
    <div class="space-y-1.5">
      @if (label()) {
        <label class="block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          {{ label() }}
          @if (required()) {
            <span class="text-danger">*</span>
          }
        </label>
      }

      <div [class]="shellClasses()">
        @if (mode() === 'single') {
          <button
            type="button"
            class="uw-date-trigger"
            [attr.aria-label]="label() || placeholder() || 'Date'"
            [attr.aria-haspopup]="'dialog'"
            [attr.aria-expanded]="isOpen()"
            [disabled]="isDisabled()"
            (click)="toggleCalendar()"
          >
            <span class="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted">
              <uw-icon name="calendar" [size]="14" />
            </span>
            <span class="truncate pr-7 text-left">
              {{ singleDisplay() || placeholder() }}
            </span>
          </button>
        } @else {
          <div class="grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              class="uw-date-trigger"
              [attr.aria-label]="startLabel()"
              [attr.aria-haspopup]="'dialog'"
              [attr.aria-expanded]="isOpen() && activeField() === 'start'"
              [disabled]="isDisabled()"
              (click)="openCalendar('start')"
            >
              <span class="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted">
                <uw-icon name="calendar" [size]="14" />
              </span>
              <span class="truncate pr-7 text-left">
                {{ rangeDisplayStart() || startPlaceholder() }}
              </span>
            </button>

            <button
              type="button"
              class="uw-date-trigger"
              [attr.aria-label]="endLabel()"
              [attr.aria-haspopup]="'dialog'"
              [attr.aria-expanded]="isOpen() && activeField() === 'end'"
              [disabled]="isDisabled()"
              (click)="openCalendar('end')"
            >
              <span class="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted">
                <uw-icon name="calendar" [size]="14" />
              </span>
              <span class="truncate pr-7 text-left">
                {{ rangeDisplayEnd() || endPlaceholder() }}
              </span>
            </button>
          </div>
        }

        @if (clearable() && hasValue()) {
          <button
            type="button"
            class="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-sunken hover:text-primary"
            [disabled]="isDisabled()"
            aria-label="Clear date"
            (click)="clear(); $event.stopPropagation()"
          >
            <uw-icon name="x" [size]="12" />
          </button>
        }

        @if (isOpen()) {
          <div class="uw-date-popover" role="dialog" aria-label="Date picker" [style.top.px]="popoverPosition().top" [style.left.px]="popoverPosition().left" [style.width.px]="popoverPosition().width">
            <div class="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
              <button
                type="button"
                class="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-sunken hover:text-primary"
                [disabled]="isDisabled()"
                aria-label="Previous month"
                (click)="previousMonth()"
              >
                <uw-icon name="chevron-left" [size]="13" />
              </button>

              <div class="min-w-0 text-center">
                <p class="typo-label m-0 text-[10px]">{{ visibleMonthLabel() }}</p>
              </div>

              <button
                type="button"
                class="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-sunken hover:text-primary"
                [disabled]="isDisabled()"
                aria-label="Next month"
                (click)="nextMonth()"
              >
                <uw-icon name="chevron-right" [size]="13" />
              </button>
            </div>

            <div class="px-3 pt-2">
              @if (mode() === 'range') {
                <div class="mb-2 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    class="uw-date-chip"
                    [class.uw-date-chip--active]="activeField() === 'start'"
                    (click)="openCalendar('start')"
                  >
                    <span class="typo-label text-[9.5px]">{{ startLabel() }}</span>
                    <span class="truncate text-[11px] font-medium text-text-primary">{{ rangeDisplayStart() || 'Pick start' }}</span>
                  </button>

                  <button
                    type="button"
                    class="uw-date-chip"
                    [class.uw-date-chip--active]="activeField() === 'end'"
                    (click)="openCalendar('end')"
                  >
                    <span class="typo-label text-[9.5px]">{{ endLabel() }}</span>
                    <span class="truncate text-[11px] font-medium text-text-primary">{{ rangeDisplayEnd() || 'Pick end' }}</span>
                  </button>
                </div>
              }

              <div class="grid grid-cols-7 gap-1 text-center">
                @for (weekday of weekdayLabels; track weekday) {
                  <span class="py-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">{{ weekday }}</span>
                }
              </div>

              <div class="mt-1 grid grid-cols-7 gap-1 pb-3">
                @for (day of calendarDays(); track day.iso) {
                  <button
                    type="button"
                    class="uw-date-day"
                    [class.uw-date-day--muted]="!day.inMonth"
                    [class.uw-date-day--today]="day.isToday"
                    [class.uw-date-day--selected]="day.isSelected"
                    [class.uw-date-day--range]="day.isInRange"
                    [class.uw-date-day--range-start]="day.isRangeStart"
                    [class.uw-date-day--range-end]="day.isRangeEnd"
                    [disabled]="day.disabled || isDisabled()"
                    [attr.aria-label]="day.iso"
                    (click)="selectDay(day)"
                  >
                    {{ day.day }}
                  </button>
                }
              </div>
            </div>
          </div>
        }
      </div>

      @if (hint()) {
        <p class="text-[10.5px] leading-snug text-text-muted">{{ hint() }}</p>
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
    }

    .uw-date-shell {
      position: relative;
      border: 1px solid var(--cp-border);
      border-radius: 0.5rem;
      background: var(--cp-surface-elevated);
      padding: 0;
      transition: border-color 120ms ease, box-shadow 120ms ease;
    }

    .uw-date-shell:focus-within {
      border-color: var(--cp-primary);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--cp-primary) 12%, transparent);
    }

    .uw-date-trigger {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
      min-height: 1.75rem;
      border: 0;
      background: transparent;
      padding: 0 1.75rem 0 1.75rem;
      color: var(--cp-text-primary);
      font-size: 0.75rem;
      line-height: 1rem;
      text-align: left;
      outline: none;
      cursor: pointer;
      appearance: none;
    }

    .uw-date-trigger:disabled {
      cursor: not-allowed;
      color: var(--cp-text-muted);
      opacity: 0.7;
    }

    .uw-date-popover {
      position: fixed;
      z-index: var(--cp-z-popover);
      overflow: hidden;
      border: 1px solid var(--cp-border);
      border-radius: 0.875rem;
      background: var(--cp-surface-elevated);
      box-shadow: var(--cp-shadow-lg);
      backdrop-filter: blur(12px);
    }

    .uw-date-chip {
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
      border: 1px solid var(--cp-border);
      border-radius: 0.75rem;
      background: var(--cp-surface);
      padding: 0.45rem 0.55rem;
      text-align: left;
      transition: border-color 120ms ease, background-color 120ms ease, color 120ms ease;
    }

    .uw-date-chip--active {
      border-color: var(--cp-primary);
      background: var(--cp-primary-soft);
    }

    .uw-date-day {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 2rem;
      border: 1px solid transparent;
      border-radius: 0.5rem;
      background: transparent;
      color: var(--cp-text-primary);
      font-size: 0.75rem;
      line-height: 1rem;
      transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease, transform 120ms ease;
    }

    .uw-date-day:hover:not(:disabled) {
      background: var(--cp-surface-sunken);
    }

    .uw-date-day:disabled {
      cursor: not-allowed;
      opacity: 0.35;
    }

    .uw-date-day--muted {
      color: var(--cp-text-muted);
    }

    .uw-date-day--today {
      border-color: var(--cp-primary);
    }

    .uw-date-day--selected {
      background: var(--cp-primary);
      color: white;
      box-shadow: var(--cp-shadow-sm);
    }

    .uw-date-day--range {
      background: var(--cp-primary-soft);
      color: var(--cp-primary);
      border-color: transparent;
    }

    .uw-date-day--range-start,
    .uw-date-day--range-end {
      background: var(--cp-primary);
      color: white;
    }
  `,
})
export class UwDate implements ControlValueAccessor {
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly mode = input<UwDateMode>('single');
  readonly label = input('');
  readonly hint = input('');
  readonly value = input<UwDateValue | null>(null);
  readonly placeholder = input('Select date');
  readonly startLabel = input('Start date');
  readonly endLabel = input('End date');
  readonly startPlaceholder = input('Start date');
  readonly endPlaceholder = input('End date');
  readonly min = input<string | null>(null);
  readonly max = input<string | null>(null);
  readonly required = input(false);
  readonly disabled = input(false);
  readonly clearable = input(true);

  readonly changed = output<UwDateValue>();

  protected readonly weekdayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  private readonly singleState = signal<UwDateSingleValue>(null);
  private readonly rangeState = signal<UwDateRangeValue>({ start: null, end: null });
  private readonly touchedState = signal(false);
  private readonly formDisabledState = signal(false);
  private readonly openState = signal(false);
  private readonly activeFieldState = signal<RangeField>('start');
  private readonly visibleMonthState = signal<Date>(this.startOfMonth(new Date()));
  private readonly syncingFromInput = signal(false);
  private readonly popoverPositionState = signal({ top: 0, left: 0, width: 320 });

  constructor() {
    effect(() => {
      const externalValue = this.value();
      const currentMode = this.mode();
      this.syncingFromInput.set(true);

      if (currentMode === 'single') {
        const nextValue = this.normalizeSingleValue(externalValue);
        this.singleState.set(nextValue);
        this.visibleMonthState.set(this.startOfMonth(this.parseDateValue(nextValue) ?? new Date()));
        this.syncingFromInput.set(false);
        return;
      }

      const nextRange = this.normalizeRangeValue(externalValue);
      this.rangeState.set(nextRange);
      const anchor = nextRange.start ?? nextRange.end;
      this.visibleMonthState.set(this.startOfMonth(this.parseDateValue(anchor) ?? new Date()));
      this.syncingFromInput.set(false);
    });
  }

  protected readonly shellClasses = computed(() => [
    'relative',
    'uw-date-shell',
    this.isDisabled() ? 'opacity-60 pointer-events-none' : '',
  ].join(' '));

  protected readonly isOpen = computed(() => this.openState());
  protected readonly activeField = computed(() => this.activeFieldState());
  protected readonly singleValue = computed(() => this.singleState() ?? '');
  protected readonly rangeValue = computed(() => this.rangeState());
  protected readonly hasValue = computed(() => {
    if (this.mode() === 'single') {
      return Boolean(this.singleState());
    }

    const range = this.rangeState();
    return Boolean(range.start || range.end);
  });
  protected readonly singleDisplay = computed(() => this.formatDisplay(this.singleState()));
  protected readonly rangeDisplayStart = computed(() => this.formatDisplay(this.rangeState().start));
  protected readonly rangeDisplayEnd = computed(() => this.formatDisplay(this.rangeState().end));
  protected readonly visibleMonthLabel = computed(() => {
    const formatter = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });
    return formatter.format(this.visibleMonthState());
  });
  protected readonly calendarDays = computed(() => this.buildCalendarDays());
  protected readonly isDisabled = computed(() => this.disabled() || this.formDisabledState());
  protected readonly popoverPosition = computed(() => this.popoverPositionState());

  private onChange: (value: UwDateValue) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: UwDateValue | null): void {
    this.syncFromExternalValue(value);
  }

  registerOnChange(fn: (value: UwDateValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabledState.set(isDisabled);
  }

  toggleCalendar(): void {
    if (this.isDisabled()) {
      return;
    }

    if (this.isOpen()) {
      this.closeCalendar();
      return;
    }

    this.openCalendar(this.mode() === 'range' ? this.activeField() : 'start');
  }

  openCalendar(field: RangeField): void {
    if (this.isDisabled()) {
      return;
    }

    this.activeFieldState.set(field);

    if (this.mode() === 'single') {
      const selected = this.parseDateValue(this.singleState());
      this.visibleMonthState.set(this.startOfMonth(selected ?? new Date()));
    } else {
      const range = this.rangeState();
      const selected = field === 'start' ? range.start : range.end;
      this.visibleMonthState.set(this.startOfMonth(this.parseDateValue(selected) ?? new Date()));
    }

    this.openState.set(true);
    this.updatePopoverPositionSoon();
  }

  previousMonth(): void {
    this.visibleMonthState.set(this.addMonths(this.visibleMonthState(), -1));
  }

  nextMonth(): void {
    this.visibleMonthState.set(this.addMonths(this.visibleMonthState(), 1));
  }

  selectDay(day: CalendarDay): void {
    if (day.disabled || this.isDisabled()) {
      return;
    }

    if (this.mode() === 'single') {
      this.singleState.set(day.iso);
      this.emitChange(day.iso);
      this.onTouched();
      this.closeCalendar();
      return;
    }

    const current = this.rangeState();
    const activeField = this.activeField();

    if (activeField === 'start' || !current.start || (current.start && current.end)) {
      this.rangeState.set({ start: day.iso, end: null });
      this.emitChange({ start: day.iso, end: null });
      this.activeFieldState.set('end');
      return;
    }

    const start = current.start ?? day.iso;
    const startDate = this.parseDateValue(start);
    const selectedDate = this.parseDateValue(day.iso);

    if (!startDate || !selectedDate) {
      this.rangeState.set({ start: day.iso, end: null });
      this.emitChange({ start: day.iso, end: null });
      return;
    }

    if (selectedDate < startDate) {
      const nextRange = { start: day.iso, end: start };
      this.rangeState.set(nextRange);
      this.emitChange(nextRange);
      this.closeCalendar();
      return;
    }

    const nextRange = { start, end: day.iso };
    this.rangeState.set(nextRange);
    this.emitChange(nextRange);
    this.closeCalendar();
  }

  clear(): void {
    if (this.isDisabled()) {
      return;
    }

    if (this.mode() === 'single') {
      this.singleState.set(null);
      this.emitChange(null);
    } else {
      const cleared: UwDateRangeValue = { start: null, end: null };
      this.rangeState.set(cleared);
      this.emitChange(cleared);
    }

    this.closeCalendar();
  }

  handleBlur(): void {
    if (!this.touchedState()) {
      this.touchedState.set(true);
    }

    this.onTouched();
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen()) {
      return;
    }

    const target = event.target as Node | null;
    if (!target) {
      return;
    }

    if (this.host.nativeElement.contains(target)) {
      return;
    }

    this.closeCalendar();
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.closeCalendar();
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    if (this.isOpen()) {
      this.updatePopoverPosition();
    }
  }

  @HostListener('window:scroll')
  protected onWindowScroll(): void {
    if (this.isOpen()) {
      this.updatePopoverPosition();
    }
  }

  private closeCalendar(): void {
    this.openState.set(false);
  }

  private updatePopoverPositionSoon(): void {
    if (typeof window === 'undefined') {
      return;
    }

    globalThis.requestAnimationFrame(() => this.updatePopoverPosition());
  }

  private updatePopoverPosition(): void {
    const rect = this.host.nativeElement.getBoundingClientRect();
    const viewportWidth = globalThis.innerWidth;
    const viewportHeight = globalThis.innerHeight;
    const gap = 8;
    const estimatedHeight = this.mode() === 'range' ? 392 : 356;
    const minWidth = 240;
    const maxWidth = Math.min(320, viewportWidth - 24);
    const availableRight = Math.max(0, viewportWidth - rect.left - 12);
    const availableLeft = Math.max(0, rect.right - 12);

    const prefersRight = availableRight >= availableLeft;
    const fitRight = availableRight >= minWidth;
    const fitLeft = availableLeft >= minWidth;

    let width = maxWidth;
    let left = rect.left;

    if (fitRight && (!fitLeft || prefersRight)) {
      width = Math.min(maxWidth, availableRight);
      left = rect.left;
    } else if (fitLeft) {
      width = Math.min(maxWidth, availableLeft);
      left = rect.right - width;
    } else if (prefersRight) {
      width = Math.min(maxWidth, Math.max(availableRight, 180));
      left = rect.left;
    } else {
      width = Math.min(maxWidth, Math.max(availableLeft, 180));
      left = rect.right - width;
    }

    const openBelow = viewportHeight - rect.bottom >= estimatedHeight || rect.top < estimatedHeight;
    const top = openBelow
      ? rect.bottom + gap
      : Math.max(12, rect.top - estimatedHeight - gap);

    left = Math.max(12, Math.min(left, viewportWidth - width - 12));

    this.popoverPositionState.set({
      top: Math.max(12, top),
      left: Math.max(12, left),
      width: Math.max(180, Math.min(width, viewportWidth - 24)),
    });
  }

  private syncFromExternalValue(value: UwDateValue | null): void {
    if (this.syncingFromInput()) {
      return;
    }

    if (this.mode() === 'single') {
      const nextValue = this.normalizeSingleValue(value);
      this.singleState.set(nextValue);
      this.visibleMonthState.set(this.startOfMonth(this.parseDateValue(nextValue) ?? new Date()));
      return;
    }

    const nextRange = this.normalizeRangeValue(value);
    this.rangeState.set(nextRange);
    const anchor = nextRange.start ?? nextRange.end;
    this.visibleMonthState.set(this.startOfMonth(this.parseDateValue(anchor) ?? new Date()));
  }

  private emitChange(value: UwDateValue): void {
    this.onChange(value);
    this.changed.emit(value);
  }

  private normalizeSingleValue(value: UwDateValue | null): UwDateSingleValue {
    if (typeof value === 'string' || value === null) {
      return this.normalizeDateString(value);
    }

    return this.normalizeDateString(value.start ?? value.end ?? null);
  }

  private normalizeRangeValue(value: UwDateValue | null): UwDateRangeValue {
    if (!value || typeof value === 'string') {
      return { start: null, end: null };
    }

    return {
      start: this.normalizeDateString(value.start),
      end: this.normalizeDateString(value.end),
    };
  }

  private normalizeDateString(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }

    const parsed = this.parseDateValue(value);
    return parsed ? this.toIsoDate(parsed) : null;
  }

  private formatDisplay(value: string | null): string {
    if (!value) {
      return '';
    }

    const parsed = this.parseDateValue(value);
    if (!parsed) {
      return value;
    }

    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(parsed);
  }

  private buildCalendarDays(): CalendarDay[] {
    const monthStart = this.startOfMonth(this.visibleMonthState());
    const gridStart = this.addDays(monthStart, -monthStart.getDay());
    const selectedSingle = this.singleState();
    const range = this.rangeState();
    const selectedStart = range.start;
    const selectedEnd = range.end;
    const selectedStartDate = this.parseDateValue(selectedStart);
    const selectedEndDate = this.parseDateValue(selectedEnd);
    const todayIso = this.toIsoDate(new Date());
    const minIso = this.normalizeDateString(this.min());
    const maxIso = this.normalizeDateString(this.max());

    return Array.from({ length: 42 }, (_, index) => {
      const date = this.addDays(gridStart, index);
      const iso = this.toIsoDate(date);
      const inMonth = date.getMonth() === monthStart.getMonth();
      const disabled = Boolean(
        (minIso && iso < minIso) ||
        (maxIso && iso > maxIso)
      );
      const isSelected = this.mode() === 'single'
        ? iso === selectedSingle
        : iso === selectedStart || iso === selectedEnd;
      const isRangeStart = Boolean(selectedStart && iso === selectedStart);
      const isRangeEnd = Boolean(selectedEnd && iso === selectedEnd);
      const isInRange = Boolean(
        this.mode() === 'range'
        && selectedStartDate
        && selectedEndDate
        && date >= selectedStartDate
        && date <= selectedEndDate
      );

      return {
        iso,
        day: date.getDate(),
        inMonth,
        disabled,
        isToday: iso === todayIso,
        isSelected,
        isRangeStart,
        isRangeEnd,
        isInRange,
      };
    });
  }

  private parseDateValue(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [year, month, day] = trimmed.split('-').map(Number);
      const parsed = new Date(year, month - 1, day);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private startOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  private addMonths(date: Date, months: number): Date {
    return new Date(date.getFullYear(), date.getMonth() + months, 1);
  }

  private addDays(date: Date, days: number): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
